import React from "react";
import { Box, Button, Paper, Stack, TextField } from "@mui/material";
import {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    Controls,
    MiniMap,
    ReactFlow,
    type Connection,
    type Edge,
    type Node,
    type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { ResolverEdgeData, ResolverFlowNode } from "./utils";

export interface ResolverFlowCanvasProps {
    nodes: ResolverFlowNode[];
    edges: Array<Edge<ResolverEdgeData>>;
    setNodes: React.Dispatch<React.SetStateAction<ResolverFlowNode[]>>;
    setEdges: React.Dispatch<React.SetStateAction<Edge<ResolverEdgeData>[]>>;
    configName: string;
    configDescription: string;
    onConfigNameChange: (value: string) => void;
    onConfigDescriptionChange: (value: string) => void;
    selectedNodeId: string | null;
    selectedEdgeId: string | null;
    onSelectedNodeIdChange: (id: string | null) => void;
    onSelectedEdgeIdChange: (id: string | null) => void;
    onAddNode: () => void;
    onPasteConfig: () => void;
    onCopyConfig: () => void;
    onConnect: (connection: Connection) => void;
    nodeTypes: NodeTypes;
}

export function ResolverFlowCanvas({
    nodes,
    edges,
    setNodes,
    setEdges,
    configName,
    configDescription,
    onConfigNameChange,
    onConfigDescriptionChange,
    selectedNodeId,
    selectedEdgeId,
    onSelectedNodeIdChange,
    onSelectedEdgeIdChange,
    onAddNode,
    onPasteConfig,
    onCopyConfig,
    onConnect,
    nodeTypes,
}: ResolverFlowCanvasProps) {
    return (
        <Paper variant="outlined" sx={{ p: 1, height: "68vh", minHeight: 500 }}>
            <Stack direction="column" width="100%" spacing={1}>
                <Stack direction="row" sx={{ mb: 1, alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <TextField
                        size="small"
                        label="Resolver Name"
                        value={configName}
                        onChange={(event) => onConfigNameChange(event.target.value)}
                        sx={{ minWidth: 320 }}
                    />
                    <TextField
                        size="small"
                        label="Description"
                        value={configDescription}
                        onChange={(event) => onConfigDescriptionChange(event.target.value)}
                        placeholder="Optional"
                        sx={{ minWidth: 200 }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" onClick={onPasteConfig} data-testid="paste-config-button">
                            Paste config
                        </Button>
                        <Button variant="outlined" onClick={onCopyConfig} data-testid="copy-config-button">
                            Copy config
                        </Button>
                        <Button variant="contained" onClick={onAddNode}>
                            Add Node
                        </Button>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        height: "calc(68vh - 88px)",
                        minHeight: 420,
                        border: "1px solid #D9D9D9",
                        borderRadius: 1,
                        width: "100%",
                        overflow: "hidden",
                        bgcolor: "#FAFBFC",
                    }}
                >
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        nodesDraggable
                        nodesConnectable
                        elementsSelectable
                        onNodesChange={(changes) => {
                            const nextNodes = applyNodeChanges(changes, nodes) as ResolverFlowNode[];
                            const keptNodes = nextNodes.length > 0 ? nextNodes : nodes;
                            setNodes(keptNodes);
                            const removedIds = new Set(
                                (changes as Array<{ type: string; id: string }>)
                                    .filter((c) => c.type === "remove")
                                    .map((c) => c.id)
                            );
                            if (removedIds.size > 0) {
                                if (selectedNodeId && removedIds.has(selectedNodeId)) {
                                    onSelectedNodeIdChange(null);
                                    onSelectedEdgeIdChange(null);
                                } else if (selectedEdgeId) {
                                    const sel = edges.find((e) => e.id === selectedEdgeId);
                                    if (sel && (removedIds.has(sel.source) || removedIds.has(sel.target))) {
                                        onSelectedEdgeIdChange(null);
                                    }
                                }
                            }
                        }}
                        onEdgesChange={(changes) => {
                            const nextEdges = applyEdgeChanges(changes, edges) as Array<Edge<ResolverEdgeData>>;
                            setEdges(nextEdges);
                            const removedIds = new Set(
                                (changes as Array<{ type: string; id: string }>)
                                    .filter((c) => c.type === "remove")
                                    .map((c) => c.id)
                            );
                            if (removedIds.size > 0 && selectedEdgeId && removedIds.has(selectedEdgeId)) {
                                onSelectedEdgeIdChange(null);
                            }
                        }}
                        onConnect={onConnect}
                        onNodeClick={(_event, node: Node) => {
                            onSelectedNodeIdChange(node.id);
                            onSelectedEdgeIdChange(null);
                        }}
                        onEdgeClick={(_event, edge: Edge) => {
                            onSelectedEdgeIdChange(edge.id);
                            onSelectedNodeIdChange(null);
                        }}
                        onPaneClick={() => {
                            onSelectedNodeIdChange(null);
                            onSelectedEdgeIdChange(null);
                        }}
                        style={{ width: "100%", height: "100%" }}
                        defaultEdgeOptions={{ animated: true }}
                        fitView
                        fitViewOptions={{ padding: 0.45 }}
                    >
                        <Background />
                        <Controls />
                        <MiniMap />
                    </ReactFlow>
                </Box>
            </Stack>
        </Paper>
    );
}
